import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { adminAPI } from '../../services/adminService';

interface Admin {
  admin_id: number;
  admin_name: string;
  admin_username: string;
  admin_email: string;
  admin_mobile: string;
  admin_type: string;
  admin_status: 'Active' | 'Inactive';
  dateadded: string;
  dateupdated: string;
}

interface AdminFormData {
  admin_name: string;
  admin_username: string;
  admin_email: string;
  admin_mobile: string;
  admin_type: string;
  admin_status: 'Active' | 'Inactive';
  admin_password?: string;
  confirmPassword?: string;
}

const AdminList: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  // Form state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Form data state
  const [formData, setFormData] = useState<AdminFormData>({
    admin_name: '',
    admin_username: '',
    admin_email: '',
    admin_mobile: '',
    admin_type: 'Admin',
    admin_status: 'Active',
    admin_password: '',
    confirmPassword: ''
  });

  // Fetch admins
  const fetchAdmins = async () => {
  try {
    setLoading(true);
    const response = await adminAPI.getAllAdmins();
    const adminsData = response || []; // ✅ fixed

    const typedAdmins: Admin[] = adminsData.map((admin: any) => ({
      admin_id: admin.admin_id,
      admin_name: admin.admin_name,
      admin_username: admin.admin_username,
      admin_email: admin.admin_email,
      admin_mobile: admin.admin_mobile,
      admin_type: admin.admin_type,
      admin_status: admin.admin_status,
      dateadded: admin.dateadded,
      dateupdated: admin.dateupdated,
    }));

    setAdmins(typedAdmins);
    console.log('Admins loaded:', typedAdmins);
  } catch (err) {
    setError('Failed to fetch admins');
    showSnackbar('Failed to fetch admins', 'error');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare the data to send
      const adminData = {
        admin_name: formData.admin_name,
        admin_username: formData.admin_username,
        admin_email: formData.admin_email,
        admin_mobile: formData.admin_mobile,
        admin_type: formData.admin_type,
        admin_status: formData.admin_status,
        ...(!editingId && { admin_password: formData.admin_password }),
        // Add required fields that might be missing
        admin_id: editingId || 0, // Temporary ID for TypeScript, will be ignored by create API
        dateadded: new Date().toISOString(),
        dateupdated: new Date().toISOString()
      } as Admin;

      if (editingId) {
        await adminAPI.updateAdmin(editingId, adminData);
      } else {
        const createdAdmin = await adminAPI.createAdmin(adminData);
        console.log('Created admin:', createdAdmin);
      }
      
      fetchAdmins();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving admin:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete admin
  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await adminAPI.deleteAdmin(id);
        showSnackbar('Admin deleted successfully', 'success');
        fetchAdmins();
      } catch (err) {
        showSnackbar('Failed to delete admin', 'error');
      }
    }
  };

  // Open edit dialog
  const handleEdit = (admin: Admin) => {
    setEditingId(admin.admin_id);
    setFormData({
      admin_name: admin.admin_name,
      admin_username: admin.admin_username,
      admin_email: admin.admin_email,
      admin_mobile: admin.admin_mobile,
      admin_type: admin.admin_type,
      admin_status: admin.admin_status,
      admin_password: '',
      confirmPassword: ''
    });
    setOpenDialog(true);
  };

  // Open create dialog
  const handleCreate = () => {
    setEditingId(null);
    setFormData({
      admin_name: '',
      admin_username: '',
      admin_email: '',
      admin_mobile: '',
      admin_type: 'Admin',
      admin_status: 'Active',
      admin_password: '',
      confirmPassword: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        
        <Typography variant="h4" gutterBottom> Admin Management </Typography>
        
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Admin
        </Button>
      </Box>

      <Paper>
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell  sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No admins found. Click "Add Admin" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => (
                  <TableRow key={admin.admin_id} hover>
                    <TableCell>{admin.admin_name}</TableCell>
                    <TableCell>{admin.admin_email}</TableCell>
                    <TableCell>{admin.admin_mobile}</TableCell>
                    <TableCell>{admin.admin_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={admin.admin_status}
                        color={admin.admin_status === 'Active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(admin)} color="primary">
                        <EditIcon />
                      </IconButton>
                      {/*<IconButton onClick={() => handleDelete(admin.admin_id)} color="error">
                        <DeleteIcon /> 
                      </IconButton>*/}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Admin' : 'Add New Admin'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  fullWidth
                  margin="normal"
                  name="admin_name"
                  label="Full Name"
                  value={formData.admin_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="admin_username"
                  label="Username"
                  value={formData.admin_username}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="admin_email"
                  label="Email"
                  type="email"
                  value={formData.admin_email}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="admin_mobile"
                  label="Mobile Number"
                  value={formData.admin_mobile}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="admin_type"
                    value={formData.admin_type}
                    onChange={handleSelectChange}
                    required
                  >
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Super Admin">Super Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="admin_status"
                    value={formData.admin_status}
                    onChange={handleSelectChange}
                    required
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="admin_password"
                  label={editingId ? 'New Password (leave blank to keep current)' : 'Password'}
                  type="password"
                  value={formData.admin_password}
                  onChange={handleInputChange}
                  required={!editingId}
                />
              </Grid>
              {!editingId && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleSelectChange}
                    required={!editingId}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AdminList;
